/**
 * Likes API — Firestore operations for comment, reply, and post likes.
 *
 * Extracted from the monolithic userOperations.js (MEDIUM-1).
 * This file is the canonical location for all like-toggle Firestore operations.
 *
 * Pattern
 * ───────
 * Each toggle function reads the current like document, then uses a Firestore
 * batch to atomically update both the like record AND the denormalised counter
 * on the target document.  This prevents count drift from partial writes.
 */

import {
  doc,
  getDoc,
  serverTimestamp,
  increment,
  runTransaction,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { ensureFreshToken } from "./authUtils";

// ─── toggleCommentLike ────────────────────────────────────────────────────────

/**
 * Toggles the current user's like on a comment.
 *
 * Uses a Firestore batch so the `commentLikes` record and the comment's
 * `likes` counter are updated atomically.
 *
 * @param {string} commentId
 * @param {string} userId  The authenticated user's UID.
 * @returns {Promise<{ isLiked: boolean, likes: number }>}
 */
export const toggleCommentLike = async (commentId, userId) => {
  try {
    await ensureFreshToken();

    const authUser = auth.currentUser;
    if (!authUser || authUser.uid !== userId) {
      throw new Error("Auth UID does not match provided userId");
    }

    const likeRef = doc(db, "commentLikes", `${commentId}_${userId}`);
    const commentRef = doc(db, "comments", commentId);

    const { isLiked, likes } = await runTransaction(db, async (tx) => {
      const [likeDoc, commentDoc] = await Promise.all([
        tx.get(likeRef),
        tx.get(commentRef),
      ]);

      if (!commentDoc.exists()) throw new Error("Comment not found");

      const currentlyLiked = likeDoc.exists();
      const currentLikes =
        typeof commentDoc.data().likes === "number"
          ? commentDoc.data().likes
          : 0;

      if (currentlyLiked) {
        tx.delete(likeRef);
        tx.update(commentRef, { likes: increment(-1) });
      } else {
        tx.set(likeRef, {
          userId,
          commentId,
          timestamp: serverTimestamp(),
        });
        tx.update(commentRef, { likes: increment(1) });
      }

      return {
        isLiked: !currentlyLiked,
        likes: currentlyLiked
          ? Math.max(0, currentLikes - 1)
          : currentLikes + 1,
      };
    });

    return { isLiked, likes };
  } catch (error) {
    console.error("[toggleCommentLike] error:", error);
    throw error;
  }
};

// ─── toggleReplyLike ──────────────────────────────────────────────────────────

/**
 * Toggles the current user's like on a reply.
 *
 * Uses a Firestore batch so the `replyLikes` record and the reply's
 * `likes` counter are updated atomically.
 *
 * @param {string} replyId
 * @param {string} userId  The authenticated user's UID.
 * @returns {Promise<{ isLiked: boolean, likes: number }>}
 */
export const toggleReplyLike = async (replyId, userId) => {
  try {
    await ensureFreshToken();

    const authUser = auth.currentUser;
    if (!authUser || authUser.uid !== userId) {
      throw new Error("Auth UID does not match provided userId");
    }

    const likeRef = doc(db, "replyLikes", `${replyId}_${userId}`);
    const replyRef = doc(db, "replies", replyId);

    const { isLiked, likes } = await runTransaction(db, async (tx) => {
      const [likeDoc, replyDoc] = await Promise.all([
        tx.get(likeRef),
        tx.get(replyRef),
      ]);

      if (!replyDoc.exists()) throw new Error("Reply not found");

      const currentlyLiked = likeDoc.exists();
      const currentLikes =
        typeof replyDoc.data().likes === "number" ? replyDoc.data().likes : 0;

      if (currentlyLiked) {
        tx.delete(likeRef);
        tx.update(replyRef, { likes: increment(-1) });
      } else {
        tx.set(likeRef, {
          userId,
          replyId,
          timestamp: serverTimestamp(),
        });
        tx.update(replyRef, { likes: increment(1) });
      }

      return {
        isLiked: !currentlyLiked,
        likes: currentlyLiked
          ? Math.max(0, currentLikes - 1)
          : currentLikes + 1,
      };
    });

    return { isLiked, likes };
  } catch (error) {
    console.error("[toggleReplyLike] error:", error.code, error.message, { replyId, userId });
    throw error;
  }
};

// ─── togglePostLike ───────────────────────────────────────────────────────────

/**
 * Toggles the current user's like on a post.
 *
 * Uses a Firestore batch so the `likes` record and the post's `likeCount`
 * counter are updated atomically.
 *
 * @param {string} postId
 * @param {string} userId  The authenticated user's UID.
 * @returns {Promise<{ isLiked: boolean, likes: number }>}
 */
export const togglePostLike = async (postId, userId) => {
  try {
    await ensureFreshToken();

    const authUser = auth.currentUser;
    if (!authUser || authUser.uid !== userId) {
      throw new Error("Auth UID does not match provided userId");
    }

    const likeRef = doc(db, "likes", `${postId}_${userId}`);
    const postRef = doc(db, "posts", postId);

    const { isLiked, likes } = await runTransaction(db, async (tx) => {
      const [likeDoc, postDoc] = await Promise.all([
        tx.get(likeRef),
        tx.get(postRef),
      ]);

      if (!postDoc.exists()) throw new Error("Post not found");

      const currentlyLiked = likeDoc.exists();
      const currentLikes =
        typeof postDoc.data().likeCount === "number"
          ? postDoc.data().likeCount
          : 0;

      if (currentlyLiked) {
        tx.delete(likeRef);
        tx.update(postRef, { likeCount: increment(-1) });
      } else {
        tx.set(likeRef, {
          userId,
          postId,
          timestamp: serverTimestamp(),
        });
        tx.update(postRef, { likeCount: increment(1) });
      }

      return {
        isLiked: !currentlyLiked,
        likes: currentlyLiked
          ? Math.max(0, currentLikes - 1)
          : currentLikes + 1,
      };
    });

    return { isLiked, likes };
  } catch (error) {
    console.error("[togglePostLike] error:", error);
    throw error;
  }
};

// ─── isPostLiked ──────────────────────────────────────────────────────────────

/**
 * Returns whether the given user has liked the given post.
 *
 * @param {string} postId
 * @param {string} userId
 * @returns {Promise<boolean>}
 */
export const isPostLiked = async (postId, userId) => {
  try {
    await ensureFreshToken();
    const likeRef = doc(db, "likes", `${postId}_${userId}`);
    const likeDoc = await getDoc(likeRef);
    return likeDoc.exists();
  } catch (error) {
    console.error("Error checking like status:", error);
    throw error;
  }
};

// ─── getPostLikes ─────────────────────────────────────────────────────────────

/**
 * Returns the denormalised like count stored on the post document.
 * This is a fast single-document read; no aggregation query is required.
 *
 * @param {string} postId
 * @returns {Promise<number>}
 */
export const getPostLikes = async (postId) => {
  try {
    await ensureFreshToken();
    const postRef = doc(db, "posts", postId);
    const postDoc = await getDoc(postRef);
    return postDoc.data()?.likeCount || 0;
  } catch (error) {
    console.error("Error getting post likes:", error);
    throw error;
  }
};
